import React from 'react';
import BrandedAlertModal from '../BrandedAlertModal/BrandedAlertModal';
import { UserAccountProps } from 'utils/types';
import styles from './JobBoardApplySuccessModal.module.scss';

export interface JobBoardApplySuccessModalProps {
  isOpen: boolean;
  user: UserAccountProps | null;
  onClose: () => void;
  onBackToJobPostings: () => void;
}

const CheckHeaderIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M5 12.5l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SuccessIllustration: React.FC = () => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
    <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="3" />
    <path
      d="M20 32l8 8 16-16"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const JobBoardApplySuccessModal: React.FC<JobBoardApplySuccessModalProps> = ({
  isOpen,
  user,
  onClose,
  onBackToJobPostings,
}) => {
  if (!isOpen || !user?.hygienist) return null;

  const { firstName, type, numJobApplications } = user.hygienist;
  const isHygienistOrAssistant = type === 0 || type === 1;
  const showTempingBoostTip =
    isHygienistOrAssistant && (numJobApplications ?? 0) <= 3;

  return (
    <BrandedAlertModal
      isOpen={isOpen}
      onClose={onClose}
      headerLabel="Application Sent"
      headerIcon={<CheckHeaderIcon />}
      illustration={<SuccessIllustration />}
      illustrationVariant="success"
      title={`Nicely done, ${firstName}!`}
      ariaLabel="Job application sent successfully"
      actions={[
        { label: 'Back to Job Postings', variant: 'primary', onClick: onBackToJobPostings },
      ]}
    >
      <p className={styles.lead}>
        You&apos;ve successfully applied for this job. Here are some details to know:
      </p>
      <ul className={styles.list}>
        <li>
          This office will contact you directly to arrange any interviews, etc.
        </li>
        <li>
          We track interview reliability, so if you schedule an interview with this office be sure
          to attend.{' '}
          <strong>
            No-showing for an interview may result in you being removed from our platform.
          </strong>
        </li>
        {isHygienistOrAssistant && (
          <li>
            If the office ever asks or contacts you about temping, be sure to log it using the
            &quot;Book/Report a Shift&quot; button at the bottom of the home calendar screen.
          </li>
        )}
        {showTempingBoostTip && (
          <li>
            Completing temping shifts with positive reviews boosts your TempStars profile, making it
            more likely you can land that dream job. If you haven&apos;t yet, it&apos;s worth booking
            and completing some temping shifts to help your profile shine above other candidates.
          </li>
        )}
      </ul>
    </BrandedAlertModal>
  );
};

export default JobBoardApplySuccessModal;
